import "./StripeCheckout.scss";

import {
  AcceptIcon,
  Alert,
  Button,
  ExclamationTriangleIcon,
  Flex,
  Segment,
  Text,
} from "@fluentui/react-northstar";
import FormState, { FormData, RemoteError } from "@shopify/react-form-state";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement, StripeCardElementOptions, StripeError } from "@stripe/stripe-js";
import { PaymentFrequency, StandardPlanLevels, TeamsUser } from "@teams/types";
import { CouponDTO, UserDTO } from "@teams/types/api";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import { useHubspotChat } from "../../actions/useHubspotChat";
import StripePoweredBy from "../../assets/powered-by-stripe.svg";
import { FetchError, FetchResult, isError } from "../../services/ChannelApi";
import { paymentsApi } from "../../services/PaymentsApi";
import { useTheme } from "../../state/ThemeProvider";
import { clientEventTracker } from "../../utils/clientTelemetry";
import * as cyberMonday from "../../utils/cyberMonday";
import {
  CheckoutPlan,
  PaymentInput,
  PlanFrequencyOption,
  getPaymentValidator,
  getStripeCardOptions,
} from "../../utils/paymentHelpers";
import { isTrialAvailable } from "../../utils/userHelpers";
import { ContactUsLink } from "../ContactUsLink";
import { FieldLabel, TextInputField } from "../fields";
import { PollyIcon } from "../PollyIcon";
import { TeamsLoader } from "../TeamsLoader";
import { PerUserPricingCalculator } from "./Pricing/PerUserPricingCalculator";
import { StripeForm } from "./StripeForm";

type StripeCheckoutProps = {
  plan: CheckoutPlan;
  authToken: string;
  user: UserDTO;
  updateSubscriptionId?: string;
  payerInfo?: { email?: string; name?: string };
  shareLink?: { tenantId?: string; teamId?: string; ownerId?: string };
  hidePayFor?: boolean;
  onOwnerBehalf?: TeamsUser;
  onCompleted: (success: boolean, trialEnd?: Date) => void;
  isTrial?: boolean;
};

const StripeCheckoutForm: React.FC<StripeCheckoutProps> = ({
  plan,
  authToken,
  user,
  onCompleted,
  updateSubscriptionId,
  payerInfo,
  onOwnerBehalf,
  isTrial,
}) => {
  const { siteVariables } = useTheme();
  const [stripeError, setStripeError] = useState<StripeError>();
  const [genericError, setGenericError] = useState<Error>();
  const [cyberMondayCouponError, setCyberMondayCouponError] = useState<string | React.ReactNode>();
  const [isTrialMode, setIsTrialMode] = useState<boolean>(false);
  const [trialError, setTrialError] = useState<string>();
  const [initialValues, setInitialValues] = useState<PaymentInput>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const { loadHubspotChat } = useHubspotChat();
  const elements = useElements();
  const stripe = useStripe();
  const { search } = useLocation();

  useEffect(() => {
    loadHubspotChat(authToken);
  }, [authToken, loadHubspotChat]);

  useEffect(() => {
    const available = isTrialAvailable(user, "agile-standup");

    if (isTrial && !available) {
      setTrialError(
        "Sorry, you are not available to start a new trial due to a previous subscription or trial."
      );
      return;
    }
  }, [isTrial, user]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("trial") === "true") {
      setIsTrialMode(true);
    }
  }, [search]);

  const onStripeSubmit = async (formDetails: FormData<PaymentInput>) => {
    if (!elements || !stripe) {
      setGenericError(new Error("stripe has not loaded yet"));
      return;
    }

    setGenericError(undefined);
    setStripeError(undefined);

    const { fields } = formDetails;

    const cardElement = elements.getElement(CardElement);

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
      billing_details: {
        name: fields.name.value,
        email: fields.email.value,
      },
    });

    if (error || !paymentMethod) {
      setStripeError(error);
      clientEventTracker.track("stripe/payment/error", user, {
        error,
        user: fields.email.value,
        plan,
        isUpdate: !!updateSubscriptionId,
        url: window.location.pathname,
      });
      return;
    }

    let licenses = fields.licenses.value;
    if (isNaN(licenses)) {
      licenses = 1;
    }

    let fetchResult: FetchResult<{ id: string; trialEnd?: string; message?: string }>;
    if (updateSubscriptionId) {
      fetchResult = await paymentsApi.updateStripePayment(
        authToken,
        paymentMethod.id,
        updateSubscriptionId
      );
    } else {
      fetchResult = await paymentsApi.createStripeSubscription(authToken, {
        planId: fields.planId.value,
        licenseQuantity: licenses,
        paymentId: paymentMethod.id,
        name: fields.name.value,
        email: fields.email.value,
        billingFrequency: fields.frequency?.value as PaymentFrequency,
        coupon: fields.coupon.value?.id,
        onBehalfOwner: onOwnerBehalf,
        isTrial: isTrialMode,
      });
    }

    if (isError(fetchResult)) {
      console.error(fetchResult);
      if (fetchResult instanceof FetchError && fetchResult.statusCode === 401) {
        setGenericError(
          new Error("Your session has expired. Please refresh and authenticate again to continue.")
        );
        clientEventTracker.trackFatalError("checkout error", user, {
          ...fetchResult,
          failureReason: "Session expired",
        });
      } else {
        setGenericError(fetchResult);
        clientEventTracker.trackFatalError("checkout error", user, {
          fetchResult,
        });
      }
    } else if (fetchResult.message) {
      console.error(fetchResult.message);
      setGenericError(new Error(fetchResult.message));
      clientEventTracker.trackFatalError("checkout error", user, {
        fetchResult,
      });
    } else {
      let trialEnd: Date | undefined;
      if (fetchResult.trialEnd) {
        trialEnd = moment(fetchResult.trialEnd).toDate();
      }
      onCompleted(true, trialEnd);
    }
  };

  useEffect(() => {
    (async () => {
      let coupon: CouponDTO | undefined;
      if (user.featureFlags.eduPromoCode) {
        setIsLoading(true);
        const result = await paymentsApi.validateCoupon(authToken, user.featureFlags.eduPromoCode);
        if (!isError(result)) {
          coupon = result;
        }
      }
      setIsLoading(false);

      setInitialValues({
        planId: "agile-standup",
        name: payerInfo?.name ?? "",
        email: payerInfo?.email ?? "",
        frequency: plan.frequency,
        licenses: plan.quantity,
        coupon,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, payerInfo, user.featureFlags.eduPromoCode, authToken]);

  const cardOptions = useMemo<StripeCardElementOptions>(() => {
    return getStripeCardOptions(siteVariables);
  }, [siteVariables]);

  const trialAndBuyButtonText = useMemo(() => {
    return isTrialMode ? "Buy Now" : "🎉 Start a Trial";
  }, [isTrialMode]);

  const showTrialAndBuyButton = useMemo(() => {
    return isTrialAvailable(user, "agile-standup");
  }, [user]);

  if (user.subscriptions.some(s => s.state === "active" && s.type === "full")) {
    return (
      <Alert
        info
        icon={<ExclamationTriangleIcon />}
        content="You already have an active Polly subscription!"
      />
    );
  }

  if (!initialValues || isLoading) {
    return <TeamsLoader isLoading loadingMessage="Loading..." />;
  }

  return (
    <Flex column gap="gap.large">
      <Flex hAlign="center" gap="gap.small" className="stripecheckout__header">
        <Flex.Item size="size.half">
          <Text align="center" size="larger" content={"Sign up for AgilePolly"} />
        </Flex.Item>
        {showTrialAndBuyButton && (
          <Button secondary size="medium" onClick={() => setIsTrialMode(!isTrialMode)}>
            {trialAndBuyButtonText}
          </Button>
        )}
      </Flex>

      {trialError && (
        <Alert
          warning
          icon={<ExclamationTriangleIcon />}
          content={<Text content={trialError} size="large" />}
        />
      )}

      <FormState
        validateOnSubmit
        initialValues={initialValues as PaymentInput}
        onSubmit={onStripeSubmit}
        validators={getPaymentValidator()}
      >
        {({ fields, submitting, submit }) => {
          const frequencyOptions = plan.frequencyOptions(fields.planId.value);
          const selectedFrequency = frequencyOptions.find(
            f => fields.frequency.value === f.frequency
          ) as PlanFrequencyOption;

          return (
            <>
              <Flex column gap="gap.large">
                <Flex className="stripecheckout__plan-info" column gap="gap.large">
                  <Text
                    content="AgilePolly for standups is billed based on monthly usage at $3 per user/month"
                    align="center"
                  />
                </Flex>

                <Text
                  content="Payment Information"
                  size="large"
                  weight="bold"
                  align="center"
                  color="brand"
                />

                <Flex wrap space="evenly">
                  <Flex column gap="gap.medium" className="stripecheckout__payment-item">
                    <TextInputField
                      field={fields.name}
                      label="Name"
                      defaultValue={user?.name}
                      required
                      fluid
                      autoFocus
                      disabled={submitting}
                      className="authoring__input-field"
                    />

                    <TextInputField
                      field={fields.email}
                      label="Billing Email"
                      required
                      fluid
                      disabled={submitting}
                      className="authoring__input-field"
                    />

                    <div className="authoring__input-field">
                      <FieldLabel label="Card Details" size="medium" />
                      <CardElement options={cardOptions} />
                    </div>

                    {stripeError && (
                      <Alert content={stripeError.message} danger variables={{ error: true }} />
                    )}

                    <FormState
                      validateOnSubmit
                      initialValues={{ coupon: cyberMonday.initCouponCode() }}
                      onSubmit={async couponFormData => {
                        fields.coupon.onChange(undefined);

                        if (!couponFormData.fields.coupon.value) {
                          return;
                        }

                        const cyberMondayError = cyberMonday.validateCoupon({
                          couponCode: couponFormData.fields.coupon.value,
                          selectedLicenses: fields.licenses.value,
                          selectedFrequency: fields.frequency.value,
                        });
                        if (cyberMondayError) {
                          setCyberMondayCouponError(cyberMondayError);
                          return;
                        }

                        const result = await paymentsApi.validateCoupon(
                          authToken,
                          couponFormData.fields.coupon.value.trim()
                        );
                        if (isError(result)) {
                          const error: RemoteError = {
                            message: result.message,
                            field: ["coupon"],
                          };
                          return [error];
                        }

                        fields.coupon.onChange(result);
                      }}
                    >
                      {couponForm => {
                        return (
                          <Flex vAlign="end" gap="gap.small">
                            <TextInputField
                              label="Promo Code"
                              maxLength={25}
                              field={couponForm.fields.coupon}
                              readOnly={couponForm.submitting}
                            />

                            <Flex.Item>
                              <div>
                                <Button
                                  data-tname="redeem-coupon"
                                  disabled={
                                    couponForm.submitting || !couponForm.fields.coupon.value
                                  }
                                  loading={couponForm.submitting}
                                  secondary
                                  icon={fields.coupon.value ? <AcceptIcon /> : undefined}
                                  content="Redeem"
                                  onClick={e => {
                                    e.preventDefault();
                                    couponForm.submit();
                                  }}
                                />
                              </div>
                            </Flex.Item>
                          </Flex>
                        );
                      }}
                    </FormState>

                    {fields.coupon.value && (
                      <Alert
                        style={{ marginTop: "1rem" }}
                        success
                        content={
                          <>
                            <Text
                              as="div"
                              weight="bold"
                              content={`Savings for ${
                                fields.coupon.value.name || fields.coupon.value.id
                              }:`}
                            />
                            <Text
                              content={`Promotion code has been applied for a ${fields.coupon.value.percentOff}% discount!`}
                            />
                          </>
                        }
                      />
                    )}

                    {cyberMondayCouponError && (
                      <Alert
                        content={cyberMondayCouponError}
                        danger
                        variables={{ error: true }}
                        icon={<ExclamationTriangleIcon />}
                      />
                    )}

                    {isTrialMode && (
                      <Alert
                        warning
                        style={{ marginTop: "1rem" }}
                        content={`Your credit card will be charged on ${moment()
                          .add(user.featureFlags.trialDays, "days")
                          .format(
                            "LL"
                          )}.  You can cancel at any time, this is a no-obligation free trial.`}
                      />
                    )}

                    {genericError && (
                      <Alert
                        content={genericError.message}
                        danger
                        variables={{ error: true }}
                        icon={<ExclamationTriangleIcon />}
                      />
                    )}

                    <Button
                      content={
                        isTrialMode
                          ? `Start ${user.featureFlags.trialDays}-day Trial`
                          : "Submit Payment"
                      }
                      style={{ marginTop: "1rem" }}
                      primary
                      fluid
                      disabled={submitting}
                      onClick={submit}
                      loading={submitting}
                      icon={!isTrialMode ? <PollyIcon icon="pay-icon" inheritColor /> : undefined}
                    />
                  </Flex>
                  <Segment
                    color="brand"
                    className="stripecheckout__payment-item"
                    styles={{ marginTop: "1rem" }}
                  >
                    <Flex column gap="gap.medium">
                      {plan.planType === "metered" && (
                        <PerUserPricingCalculator
                          taxPercentage={0}
                          defaultUsers={fields.licenses.value}
                          coupon={fields.coupon.value}
                          perUserPrice={selectedFrequency.price}
                        />
                      )}
                      <Text content="* Prices are in USD" size="small" align="center" />
                    </Flex>
                  </Segment>
                </Flex>
              </Flex>

              <Flex space="between">
                <Flex.Item>
                  <a href="https://www.stripe.com" target="_blank" rel="noopener noreferrer">
                    <img src={StripePoweredBy} alt="Powered by Stripe" height={20} />
                  </a>
                </Flex.Item>

                <Flex.Item align="center">
                  <ContactUsLink user={user} label="Contact Sales" />
                </Flex.Item>
              </Flex>
            </>
          );
        }}
      </FormState>
    </Flex>
  );
};

export const StripeAgileCheckoutPage: React.FC<StripeCheckoutProps> = props => {
  return (
    <StripeForm>
      <StripeCheckoutForm {...props} />
    </StripeForm>
  );
};

type PlanLevelInfoProps = {
  plan: StandardPlanLevels;
  isSelected: boolean;
  disabled: boolean;
  onSelected: (plan: StandardPlanLevels) => void;
};

export const PlanLevelInfo: React.FC<PlanLevelInfoProps> = ({
  plan,
  disabled,
  onSelected,
  isSelected,
}) => {
  const planDurationText = useMemo(() => {
    switch (plan.paymentFrequency) {
      case "monthly":
        return "(1-month subscription)";
      case "quarterly":
        return "(3-month subscription)";
      case "semi_annually":
        return "(6-month subscription)";
      case "yearly":
        return "(12-month subscription)";
      default:
        return "(Custom Terms)";
    }
  }, [plan.paymentFrequency]);

  return (
    <Segment color="brand" className="stripecheckout__planlevelinfo">
      <Flex column gap="gap.small" className="stripecheckout__planlevelinfo-content">
        <Text align="center" content={plan.name} weight="bold" size="larger" color="brand" />
        <Text align="center" content={planDurationText} weight="semilight" size="small" />

        <Text
          align="center"
          content={`${
            plan.id === "teams-pro-unlimited" ? "Unlimited" : plan.responseQuantity
          } Responses per month`}
          weight="bold"
        />

        <Flex.Item grow>
          <Flex vAlign="center" hAlign="center">
            <Text align="center" size="large" content={`$${plan.monthlyPrice}/month`} />
          </Flex>
        </Flex.Item>

        <Button
          fluid
          disabled={disabled}
          primary={isSelected}
          secondary={!isSelected}
          onClick={() => onSelected(plan)}
          icon={isSelected ? <PollyIcon icon="correct-icon" inheritColor /> : undefined}
          content={isSelected ? "Selected" : "Select plan"}
        />
      </Flex>
    </Segment>
  );
};
